import { useCallback } from "react";
import { isEqual } from "@util/utils";
import { REVENUE_TYPE } from "@icoach/constants/dataEnums";
import useOrderCheckoutApi from "@apis/useOrderCheckoutApi";
import { useOrderCart } from "./useOrderCart";

/**
 * 智能系統月費同步 Hook
 * 處理既存月費與智能月費的關聯邏輯：
 * 1. 當購物車中既存月費價格變更時，檢查並添加對應的智能月費到獨立的批次訂單購物車
 * 2. 當既存月費從購物車移除時，同時移除對應的智能月費
 *
 * 注意：智能月費不屬於 NewOrderCart(店鋪現場收入)，而是獨立的批次扣款訂單
 *
 * @param {Object} options - Hook 配置選項
 * @param {string|number} [options.memberID] - 會員 ID
 * @param {boolean} [options.isUsingDeviceOrder=false] - 是否啟用智能系統
 * @returns {Object} 返回處理方法和智能月費購物車狀態
 */
const useDeviceOrderFeeSync = ({ memberID, isUsingDeviceOrder = false }) => {
	const { postOrdersCheckoutCheckDeviceOrderApi } = useOrderCheckoutApi();
	// 管理智能月費
	const smartFeeCart = useOrderCart([]);

	/**
	 * 檢查是否為既存月費
	 * @param {Object} itemData - 項目資料
	 * @returns {boolean}
	 */
	const isExistingMonthlyFee = useCallback(
		itemData => {
			return isEqual(itemData.revenueType, REVENUE_TYPE.MONTHLYFEE);
		},
		// eslint-disable-next-line
		[],
	);

	/**
	 * 檢查智能月費是否存在（透過 relatedFeeName）
	 * @param {string} relatedFeeName - 關聯的既存月費名稱
	 * @returns {boolean}
	 */
	const isSmartFeeExists = useCallback(
		relatedFeeName => {
			return smartFeeCart.cartItems.some(item => item.relatedFeeName === relatedFeeName);
		},
		// eslint-disable-next-line
		[smartFeeCart.cartItems],
	);

	/**
	 * 處理智能月費 API 回應，將智能月費加入獨立的批次訂單購物車
	 * @param {Array} response - API 回應資料
	 * @param {string} relatedFeeName - 關聯的既存月費名稱
	 */
	const addSmartFeesToCart = useCallback(
		(response, relatedFeeName) => {
			if (!response?.length) return;

			// 過濾出有效的智能月費（assignPrice > 0）
			const validSmartFees = response.filter(item => item.assignPrice > 0);

			// 先移除同 relatedFeeName 的舊智能月費（避免重複）
			smartFeeCart.deleteItemsWhere(item => item.relatedFeeName === relatedFeeName);

			// 添加新的智能月費，設置 relatedFeeName
			validSmartFees.forEach(smartFee => {
				smartFeeCart.addItem({
					name: smartFee.name,
					productTypeText: smartFee.productTypeText || "",
					assignPrice: smartFee.assignPrice || 0,
					revenueType: smartFee.revenueType, // 使用 API 回應中的 revenueType
					notes: smartFee.notes || "",
					relatedFeeName: relatedFeeName, // 關聯的既存月費名稱
				});
			});
		},
		// eslint-disable-next-line
		[smartFeeCart],
	);

	/**
	 * 處理既存月費價格變更,檢查是否需要添加智能月費
	 *
	 * @param {Object} itemData - 既存月費項目資料
	 * @param {number} price - 價格
	 */
	const handleMonthlyFeeAdded = useCallback(
		async (itemData, price) => {
			// 未啟用智能系統則不處理
			if (!isUsingDeviceOrder) return;

			// 不是既存月費則不處理
			if (!isExistingMonthlyFee(itemData)) {
				return;
			}

			// 不是會員則不處理
			if (!memberID) {
				return;
			}

			try {
				// 單筆既存月費資料（目前操作都是單筆更新）
				const monthlyFeeItem = [
					{
						revenueType: itemData.revenueType,
						name: itemData.name,
						assignPrice: price,
						notes: itemData.notes || "",
					},
				];

				const resp = await postOrdersCheckoutCheckDeviceOrderApi(memberID, monthlyFeeItem);
				if (resp) addSmartFeesToCart(resp, itemData.name);
			} catch (error) {
				console.error("檢查智能月費失敗:", error);
			}
		},
		// eslint-disable-next-line
		[isExistingMonthlyFee, isUsingDeviceOrder, memberID, postOrdersCheckoutCheckDeviceOrderApi, addSmartFeesToCart, isSmartFeeExists],
	);

	/**
	 * 處理既存月費從購物車移除
	 * 當既存月費從購物車移除時，同時移除對應的智能月費
	 *
	 * @param {Object} itemData - 既存月費項目資料
	 */
	const handleMonthlyFeeRemoved = useCallback(
		itemData => {
			// 未啟用智能系統則不處理
			if (!isUsingDeviceOrder) return;

			// 不是既存月費則不處理
			if (!isExistingMonthlyFee(itemData)) {
				return;
			}

			// 刪除關聯的智能月費（使用 relatedFeeName）
			const deletedFeeName = itemData.name;
			smartFeeCart.deleteItemsWhere(item => item.relatedFeeName === deletedFeeName);
		},
		// eslint-disable-next-line
		[isExistingMonthlyFee, isUsingDeviceOrder, smartFeeCart],
	);

	/**
	 * 包裝 updatePrice 方法，在價格變更時檢查智能月費
	 * @param {Function} originalUpdatePrice - 購物車裡的 updatePrice 方法（接收 identifier, assignPrice）
	 * @returns {Function} 包裝後的 updatePrice 方法（接收 item, newPrice）
	 */
	const wrapUpdatePrice = useCallback(
		originalUpdatePrice => {
			return (item, newPrice) => {
				// 未啟用智能系統，僅透傳原始更新
				if (!isUsingDeviceOrder) {
					originalUpdatePrice(item, newPrice);
					return;
				}

				// 先執行原始的價格更新
				originalUpdatePrice(item, newPrice);

				// 如果是既存月費，處理智能月費邏輯
				if (!item.hasOwnProperty("productID") && isExistingMonthlyFee(item)) {
					const price = Number(newPrice) || 0;
					const feeName = item.name;

					// 如果價格為 0，檢查是否存在智能月費
					if (price === 0) {
						// 價格為 0，移除關聯的智能月費（如果存在）
						if (isSmartFeeExists(feeName)) {
							smartFeeCart.deleteItemsWhere(smartFee => smartFee.relatedFeeName === feeName);
						}
					} else {
						handleMonthlyFeeAdded(item, price);
					}
				}
			};
		},
		// eslint-disable-next-line
		[handleMonthlyFeeAdded, isExistingMonthlyFee, isUsingDeviceOrder, isSmartFeeExists, smartFeeCart],
	);

	/**
	 * 包裝 deleteItem 方法，在刪除時檢查是否需要移除智能月費
	 * @param {Function} originalDeleteItem - 原始的 deleteItem 方法
	 * @returns {Function} 包裝後的 deleteItem 方法
	 */
	const wrapDeleteItem = useCallback(
		originalDeleteItem => {
			return item => {
				// 未啟用智能系統，直接透傳刪除
				if (!isUsingDeviceOrder) {
					originalDeleteItem(item);
					return;
				}

				// 如果是既存月費，使用 handleMonthlyFeeRemoved（它會處理智能月費的移除）
				if (!item.hasOwnProperty("productID") && isExistingMonthlyFee(item)) {
					handleMonthlyFeeRemoved(item);
				}
				// 無論是否既存月費，都刪除主購物車該項
				originalDeleteItem(item);
			};
		},
		// eslint-disable-next-line
		[handleMonthlyFeeRemoved, isExistingMonthlyFee, isUsingDeviceOrder],
	);

	return {
		deviceOrderCartItems: smartFeeCart.cartItems,
		deviceOrderCartTotal: smartFeeCart.total,
		handleMonthlyFeeAdded,
		handleMonthlyFeeRemoved,
		wrapUpdatePrice,
		wrapDeleteItem,
	};
};

export default useDeviceOrderFeeSync;
